import React, { useEffect, useState } from 'react';
import './HomePage.css';
import api from '../api';

const formatStartDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const date = new Date(dateTimeStr);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.toLocaleDateString('en-US', { day: 'numeric' });
  const year = date.toLocaleDateString('en-US', { year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${weekday} ${month} ${day}, ${year} at ${time}`;
};

const preventImageInteraction = (event) => {
  event.preventDefault();
};

export default function HomePage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventError, setEventError] = useState(false);

  useEffect(() => {
    api.get('/api/events/current')
      .then((res) => {
        setEvent(res.data);
        setLoading(false);
      })
      .catch(() => {
        setEventError(true);
        setLoading(false);
      });
  }, []);

  const dateStr = event ? formatStartDateTime(event.date_time) : '';
  const endTimeStr = event?.end_time
    ? new Date(event.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <div className="home-page">
      <section className={`notice${event?.is_cancelled ? ' notice-cancelled' : ''}`} id="notice" aria-live="polite">
        <div className="notice-label">Next gathering</div>
        {loading && <p>Checking for the next event...</p>}
        {!loading && eventError && <p>Event details are temporarily unavailable. Visit the Events page for updates.</p>}
        {!loading && !eventError && !event && <p>No upcoming event is scheduled. Visit the Events page for what is ahead.</p>}
        {!loading && !eventError && event?.is_cancelled && (
          <div className="notice-details">
            <strong>This event has been cancelled</strong>
            <span>{event.event_name}</span>
            <span>{dateStr}{endTimeStr && ` - ${endTimeStr}`}</span>
            {event.cancelled_reason && <span className="cancelled-reason">Reason: {event.cancelled_reason}</span>}
          </div>
        )}
        {!loading && !eventError && event && !event.is_cancelled && (
          <div className="notice-details">
            <strong>{event.event_name}</strong>
            <span>{dateStr}{endTimeStr && ` - ${endTimeStr}`}</span>
            <span>Address: {event.address}</span>
          </div>
        )}
        <a className="notice-link" href="/events">View all events <span aria-hidden="true">↗</span></a>
      </section>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div
            className="hero-image-wrap protected-image"
            onContextMenu={preventImageInteraction}
            onDragStart={preventImageInteraction}
          >
            <img
              className="spanner"
              src="/images/rsv38.png"
              alt="Ramakrishna Vedanta Center of Charlotte"
              draggable={false}
              onContextMenu={preventImageInteraction}
              onDragStart={preventImageInteraction}
            />
          </div>
        </section>

        <section className="statement" id="statement" aria-label="Ramakrishna slogan">
          <img className="slogan" src="/images/rk_slogan.png" alt="Ramakrishna Slogan" />
        </section>

        <section className="content" id="about">
          <div className="about-copy" id="contsec1">
            <p className="eyebrow">Vedanta Center of Charlotte</p>
            <h1 className="about-tagline" id="hero-title">A quiet place to seek what is true.</h1>
            <h2>One truth, many names and forms.</h2>
            <p>
              <strong>Ramakrishna Vedanta Center of Charlotte</strong>&nbsp;is dedicated to practice the&nbsp;
              <a href="https://belurmath.org/vedanta-and-indian-culture/"><strong>Ideals of Vedanta</strong></a>&nbsp;as
              presented to the modern world by Sri Ramakrishna, Sri Sarada Devi, and Swami Vivekananda.
              Vedanta states that the underlying substance behind all phenomena, names, and forms is one.
              A few thousand years ago, the scriptures of India affirmed that <q>Truth is one.</q> Vedanta does not
              ask us to believe in any particular form or idea of God, or subscribe to any particular religion —
              it only asks that we be bold and sincere in our search for the Truth.
            </p>
            <div className="history-block">
              <p className="eyebrow">About us</p>
              <p>
                The Ramakrishna Vedanta Center Charlotte was founded in May 2018. The spiritual Head of this center is Swami Ishatmananda (the head of Vedanta Society of Chicago). The center has been formed by a community of devotees from Charlotte metro who follow the Sri Ramakrishna order. It is a part of the Ramakrishna Movement, following the ideals set forth by Sri Ramakrishna, Sri Sarada Devi, and Swami Vivekananda. Through talks, lectures, inter-faith meetings, spiritual discourse, and religious celebrations we aim to translate into action the lofty philosophy of Swami Vivekananda — <i>to make people aware of their inner divinity and to help them to manifest it in every moment of life.</i>
              </p>
            </div>
          </div>

          <aside className="ideals-panel" aria-labelledby="ideals-title">
            <p className="eyebrow">In practice</p>
            <h2 id="ideals-title">The ideals we return to.</h2>
            <ul>
              <li><span>01</span><strong>Unity behind names and forms</strong></li>
              <li><span>02</span><strong>A bold, sincere search for Truth</strong></li>
              <li><span>03</span><strong>The ideals of Sri Ramakrishna, Sri Sarada Devi, and Swami Vivekananda</strong></li>
              <li><span>04</span><strong>Manifesting inner divinity in daily life</strong></li>
            </ul>
          </aside>
        </section>

        <section className="mentor-section" aria-labelledby="mentor-title">
          <div className="mentor-portrait">
            <p className="eyebrow">Mentor and guide</p>
            <div className="mentor-image-wrap"><img src="/images/Ishatmananda.PNG" alt="Swami Ishatmananda" /></div>
            <p className="mentor-name"><strong>Swami Ishatmananda</strong></p>
            <p className="mentor-role">Head of Vedanta Society<br />Chicago, IL</p>
          </div>
          <div className="mentor-copy" id="contsec2">
            <h2 id="mentor-title">A living tradition, carried forward.</h2>
          </div>
          <div className="tradition-images" aria-label="The Ramakrishna tradition">
            <img src="/images/Ramakrishna-Mission-Transparent.png" alt="Ramakrishna Mission" />
            <img src="/images/SaradaMa.png" alt="Sri Sarada Devi" />
          </div>
        </section>

        <section className="contact-section" id="contactus">
          <div>
            <p className="eyebrow">Contact Us For More Details:</p>
          </div>
          <div className="contact-details">
            <p><strong>Facebook</strong><a href="https://www.facebook.com/groups/1150517765574184">Vedanta Center Charlotte</a></p>
            <p><strong>Email</strong><a href="mailto:info@vedantacentercharlotte.org">info@vedantacentercharlotte.org</a></p>
          </div>
        </section>

        <nav className="explore-links" aria-label="Explore Vedanta Center">
          <span>Explore</span>
          <a href="/events">Events</a><a href="/media">Gallery</a><a href="/lectures">Lectures</a>
          <a href="/books">Books</a><a href="/donate">Donate</a><a href="/contact">Contact</a><a href="/blogs">Blogs</a>
        </nav>
      </main>
    </div>
  );
}
